import './StatisticsPro.sass';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useRoute } from 'react-router5';

import { Button, Header, Placeholder, ScrollView } from '$uikit';
import { Loader, MetaTags } from '$shared/components';
import { CurrencyEnum } from '$store/models';
import { toastActions } from '$store/toast';
import { useTranslator } from '$hooks';

import { fetchLinksStats, LinkJoinRow, LinkMeta } from './StatisticsProLinks.api';
import { DAY_SECONDS, presetToRange } from './utils/dateRange';
import { downloadCsv } from './utils/csvExport';
import { DateRangePreset } from './components/DateRangeSection';
import { DateButton } from './components/DateButton';
import { PromoLinksButton } from './components/PromoLinksButton';
import { LinksChart } from './components/LinksChart';
import { StatsTable } from './components/StatsTable';

// day -> linkId -> joins; aggregate схлопывает все ссылки в ключ 0,
// weekly укладывает дни в недельные корзины (понедельник — начало недели).
function bucketize(
  rows: LinkJoinRow[],
  [from, to]: [number, number],
  aggregate: boolean,
  weekly: boolean,
): { days: number[]; values: Record<number, Record<string, number>> } {
  const bucketOf = (day: number): number => {
    if (!weekly) return day;
    const dse = Math.floor(day / DAY_SECONDS);
    const offset = ((dse % 7) + 3) % 7;
    return (dse - offset) * DAY_SECONDS;
  };

  const buckets: number[] = [];
  const step = weekly ? 7 * DAY_SECONDS : DAY_SECONDS;
  for (let b = bucketOf(from); b <= to; b += step) {
    buckets.push(b);
  }

  const map: Record<number, Record<string, number>> = {};
  for (const b of buckets) {
    map[b] = {};
  }
  for (const row of rows) {
    if (row.date < from || row.date > to) continue;
    const target = map[bucketOf(row.date)];
    if (!target) continue;
    const key = aggregate ? 0 : row.linkId;
    target[key] = (target[key] ?? 0) + row.count;
  }

  return { days: buckets, values: map };
}

export const StatisticsProLinks = () => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const { route } = useRoute();

  const routeLinkId = Number(route?.params?.linkId) || 0;

  const [preset, setPreset] = useState<DateRangePreset>('30d');
  const [range, setRange] = useState<[number, number]>(() => presetToRange('30d', 0, 0));
  const [promoLinkIds, setPromoLinkIds] = useState<number[]>(() =>
    routeLinkId > 0 ? [routeLinkId] : [],
  );

  useEffect(() => {
    if (routeLinkId > 0) {
      setPromoLinkIds((prev) =>
        prev.length === 1 && prev[0] === routeLinkId ? prev : [routeLinkId],
      );
    }
  }, [routeLinkId]);

  const [links, setLinks] = useState<LinkMeta[]>([]);
  const [rows, setRows] = useState<LinkJoinRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setHasError(false);
    fetchLinksStats({ from: range[0], to: range[1], promoLinkIds })
      .then((data) => {
        if (cancelled) return;
        setLinks(data.links);
        setRows(data.rows);
        setLoadedOnce(true);
      })
      .catch((e) => {
        if (cancelled) return;
        setHasError(true);
        dispatch(toastActions.setToastFail(e?.message || 'Failed to load statistics'));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range, promoLinkIds, reloadKey, dispatch]);

  const handlePresetChange = useCallback(
    (next: DateRangePreset, startDay: number, endDay: number) => {
      setPreset(next);
      setRange(presetToRange(next, startDay, endDay));
    },
    [],
  );

  const aggregate = !promoLinkIds.length && links.length > 0;

  const chartLinks = useMemo<LinkMeta[]>(
    () =>
      aggregate
        ? [
            {
              id: 0,
              name: t('statistics_pro_links_all_links', { defaultValue: 'All links' }),
            },
          ]
        : links,
    [aggregate, links, t],
  );

  const { days, values } = useMemo(
    () => bucketize(rows, range, aggregate, (range[1] - range[0]) / DAY_SECONDS > 90),
    [range, rows, aggregate],
  );
  const { days: tableDays, values: tableValues } = useMemo(
    () => bucketize(rows, range, aggregate, false),
    [range, rows, aggregate],
  );

  const tableCategories = useMemo(
    () => chartLinks.map((link) => String(link.id)),
    [chartLinks],
  );
  const tableLabels = useMemo(() => {
    const map: Record<string, string> = {};
    for (const link of chartLinks) map[String(link.id)] = link.name;
    return map;
  }, [chartLinks]);

  const handleExport = useCallback(() => {
    downloadCsv({
      rows: tableDays.map((d) => ({ date: d, values: tableValues[d] ?? {} })),
      categories: tableCategories,
      othersTypes: [],
      labels: tableLabels,
      filename: `promo-links-${tableDays[0] ?? 'empty'}-${
        tableDays[tableDays.length - 1] ?? 'empty'
      }.csv`,
    });
  }, [tableDays, tableValues, tableCategories, tableLabels]);

  return (
    <>
      <MetaTags
        title={t('statistics_pro_links_title', { defaultValue: 'Referral links' })}
      />
      <Header showBackButton>
        {t('statistics_pro_links_title', { defaultValue: 'Referral links' })}
      </Header>
      <ScrollView className="StatisticsPro">
        <div className="StatisticsProControls">
          <DateButton
            preset={preset}
            startDay={range[0]}
            endDay={range[1]}
            onChange={handlePresetChange}
          />
          <PromoLinksButton value={promoLinkIds} onChange={setPromoLinkIds} />
        </div>

        {isLoading && !loadedOnce ? (
          <Loader isWrapped />
        ) : hasError && !loadedOnce ? (
          <Placeholder
            title={t('failed_title', { defaultValue: 'Something went wrong' })}
            actions={
              <Button size="small" onClick={() => setReloadKey((k) => k + 1)}>
                {t('failed_button', { defaultValue: 'Try again' })}
              </Button>
            }
          >
            {t('statistics_pro_links_error', {
              defaultValue: "Couldn't load link statistics. Please try again.",
            })}
          </Placeholder>
        ) : (
          <>
            <LinksChart days={days} values={values} links={chartLinks} />

            <StatsTable
              days={tableDays}
              values={tableValues}
              selected={tableCategories}
              othersTypes={[]}
              labels={tableLabels}
              currency={CurrencyEnum.EUR}
              integer
              totalLabel={t('statistics_pro_total', { defaultValue: 'Total' })}
              onExport={handleExport}
            />
          </>
        )}
      </ScrollView>
    </>
  );
};
